// React 19 対応: antd 5 の静的メソッド（Modal.confirm / message / notification）は
// react-dom から createRoot/render が削除された React 19 では無言で失敗するため、
// 公式 patch を最初に読み込んで unstableSetRender を react-dom/client に差し替える。
import '@ant-design/v5-patch-for-react-19';
import '../styles/globals.css';
// antd 5 は CSS-in-JS（自動注入）のため antd/dist/antd.css の読み込みは不要・廃止
import 'tailwindcss/tailwind.css';
import '../styles/login.css';
import type { AppProps } from 'next/app';
import React from 'react';
import { ConfigProvider } from 'antd';
import UserProvider from '@/context/user';
// Registers the global axios interceptors before the first API call (maintenance 503 handling).
import '@/config/AxiosConfig';
import { getMaintenanceStatus, MAINTENANCE_PATH } from '@/apis/MaintenanceApi';

// アプリ全体の既定フォント（Ant Design コンポーネントにも適用）
const NOTO_SANS_FONT =
    "'Noto Sans', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, " +
    "Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

// Paths that must never be stored as currentPath.
// Storing '/' causes index.tsx to redirect to itself (infinite loop).
// Auth/error pages are not meaningful navigation destinations.
const SKIP_STORE_PATHS = ['/', '/samlLogin', '/login', '/401', '/403', MAINTENANCE_PATH];
// Sign-in form for the maintenance test account. Not linked anywhere (the operator knows the URL).
const ACCOUNT_LOGIN_QUERY = /[?&]maint=1/;

function MyApp({ Component, pageProps }: AppProps) {
    const router = useRouter();
    const [accountSession, setAccountSession] = useState(false);

    useEffect(() => {
        storePathValues();
        checkMaintenance();
    }, [router.asPath]);

    // During maintenance every page shows the notice, except for the maintenance test account.
    async function checkMaintenance() {
        if (router.pathname === MAINTENANCE_PATH) return;
        const status = await getMaintenanceStatus();
        setAccountSession(!!status?.enabled && !!status?.test_account_session);
        const accountLogin = router.pathname === '/login' && ACCOUNT_LOGIN_QUERY.test(router.asPath);
        if (status?.enabled && !status.test_account_session && !accountLogin) router.replace(MAINTENANCE_PATH);
    }

    function storePathValues() {
        const storage = globalThis?.sessionStorage;
        if (!storage) return;
        // Skip root, auth, and error pages — they are not restore destinations.
        if (SKIP_STORE_PATHS.includes(router.pathname)) return;
        const prevPath = storage.getItem('currentPath');
        storage.setItem('prevPath', prevPath || '');
        // Store router.pathname (actual page file path) instead of location.pathname
        // (display URL). This prevents infinite redirect loops on page refresh when
        // the browser URL is a display alias (e.g. /analysis/evidence-level)
        // that does not correspond to a real Next.js page file.
        storage.setItem('currentPath', router.pathname);
        storage.setItem('currentHref', router.pathname);
    }

    return (
        <ConfigProvider theme={{ token: { fontFamily: NOTO_SANS_FONT } }}>
            <UserProvider>
                {accountSession && (
                    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000, padding: '4px 12px', background: '#fa8c16', color: '#fff', fontSize: 12, textAlign: 'center' }}>
                        メンテナンス中（テスト用アカウント） / Maintenance — test account
                    </div>
                )}
                <Component {...pageProps} />
            </UserProvider>
        </ConfigProvider>
    );
}
export default MyApp;
